import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDirtyDialogGuard } from '@/features/patient-record/hooks/useDirtyDialogGuard';
import { useEditableDraft } from '@/features/patient-record/hooks/useEditableDraft';
import {
  useVariantMutations,
  type VariantInput,
} from '@/features/patient-record/hooks/useVariantMutations';
import type { GeneticVariantViewModel } from '@/features/patient-record/types';

import { VariantField } from './fields/VariantField';
import {
  draftToDto,
  dtoToDraft,
  emptyDraft,
  type VariantDraft,
} from './variantForm';
import { orderedVariantFields, VARIANT_FIELD_GROUPS } from './variantFields';

export type VariantFormDialogProps =
  | {
      readonly guid: string;
      readonly mode: 'create';
      readonly variant?: never;
      readonly onClose: () => void;
    }
  | {
      readonly guid: string;
      readonly mode: 'edit';
      readonly variant: GeneticVariantViewModel;
      readonly onClose: () => void;
    };

/** Keys owned by variant-type-specific groups — cleared when their type is hidden. */
const TYPE_SPECIFIC_KEYS = new Set(
  VARIANT_FIELD_GROUPS.filter((g) => g.visibleFor).flatMap((g) =>
    g.fields.map((f) => f.key),
  ),
);

/** A draft has committable content once a gene, cDNA, or variant type is set. */
const hasContent = (d: VariantDraft): boolean =>
  (d.geneSymbol as string).length > 0 ||
  (d.cdna as string).length > 0 ||
  ((d.variantType as string) || '').length > 0;

/**
 * Create / edit a genetic variant. Fields are declared in `variantFields.ts`
 * and rendered generically here; the Core group is always shown and each
 * variant-type group appears only when `variantType` matches. On submit the
 * draft is pruned of hidden type-specific fields, then reconstructed into a
 * full `GeneticInfoDTO` (PUT is a full replace) preserving fields the form
 * doesn't surface.
 */
export function VariantFormDialog(props: VariantFormDialogProps): ReactElement {
  const { t } = useTranslation();
  const { create, update } = useVariantMutations(props.guid);
  const label = t('patient.geneticInfo.formLabel', { defaultValue: 'Genetic variant' });

  // Build the full DTO for a draft: prune type-specific fields hidden for the
  // draft's current variant type, then reconstruct the DTO (preserving fields
  // the form doesn't surface via the original row on edit).
  const toInput = (d: VariantDraft): VariantInput => {
    const variantType = (d.variantType as string) || '';
    const visibleKeys = new Set(orderedVariantFields(variantType).map((f) => f.key));
    const pruned: VariantDraft = { ...d };
    for (const key of TYPE_SPECIFIC_KEYS) {
      if (!visibleKeys.has(key)) {
        pruned[key] = Array.isArray(d[key]) ? [] : '';
      }
    }
    const base = props.mode === 'edit' ? props.variant.raw : undefined;
    return draftToDto(pruned, base);
  };

  // Commit ONLY — never closes. Used by the primitive's registry flush.
  const commit = (d: VariantDraft): Promise<unknown> =>
    props.mode === 'create'
      ? create.mutateAsync(toInput(d))
      : update.mutateAsync({ id: props.variant.id, input: toInput(d) });

  // PTV2-1327: one primitive owns the draft. Autosave is off — a variant is
  // created/updated only by the explicit submit; the guard still catches a
  // dirty dismiss / refresh.
  const form = useEditableDraft<VariantDraft>({
    initial: props.mode === 'edit' ? dtoToDraft(props.variant.raw) : emptyDraft(),
    label,
    autosave: false,
    canSave: (d) => hasContent(d),
    onSave: (d) => commit(d),
  });
  const { draft } = form;

  const variantType = (draft.variantType as string) || '';
  const fields = useMemo(() => orderedVariantFields(variantType), [variantType]);

  const pending = create.isPending || update.isPending;

  const patch = (next: VariantDraft): void => {
    form.setDraft((prev) => ({ ...prev, ...next }));
  };

  // Explicit submit: commit AND close.
  const submitAndClose = (): void => {
    if (!form.canSave) return;
    void commit(draft).then(props.onClose).catch(() => undefined);
  };

  const title =
    props.mode === 'create'
      ? t('patient.geneticInfo.createTitle')
      : `${props.variant.gene} ${props.variant.variant}`.trim();

  // PTV2-1301: warn Save / Discard / Keep editing on dismiss (and refresh) while
  // the form is dirty. `register: false` — the primitive already registers.
  const guard = useDirtyDialogGuard({
    dirty: form.isDirty,
    canSave: form.canSave,
    saving: pending,
    label,
    onSave: submitAndClose,
    onClose: props.onClose,
    register: false,
  });

  return (
    <>
    <Dialog open {...guard.rootProps}>
      <DialogContent
        className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl"
        {...guard.contentProps}
      >
        <DialogHeader className="shrink-0 px-6 pt-6 pb-4">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form
          ref={form.containerProps.ref}
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            submitAndClose();
          }}
        >
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4">
            <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
              {fields.map((field) => (
                <div
                  key={field.key}
                  className={field.full ? 'sm:col-span-2 lg:col-span-3' : undefined}
                >
                  <VariantField
                    field={field}
                    draft={draft}
                    onPatch={patch}
                    disabled={pending}
                  />
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="shrink-0 border-t border-border px-6 py-4">
            <Button
              type="button"
              variant="ghost"
              onClick={guard.requestClose}
              disabled={pending}
            >
              {t('ui.dialog.close')}
            </Button>
            <Button
              type="submit"
              disabled={!form.canSave || pending}
              loading={pending}
              loadingText={t('patient.dirtyFooter.saving')}
            >
              {props.mode === 'create'
                ? t('patient.geneticInfo.addVariant')
                : t('patient.geneticInfo.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    {guard.confirm}
    </>
  );
}
